<template>
    <el-container direction="vertical" class="spreadsheet">
        <deal-grid-header
            :filter="filterModel"
            :fields="fieldsNames"
            :grid-options="gridOptions"
            :visible-options="headerOptions"
            :title="title"
            :route-pipeline-id="$route.query.pipeline_id"
            @filterApplied="filterApplied"
            @excelAction="exportDeals"
        />
        <el-row type="flex" align="top" class="spreadsheet-table" v-loading="loading">
            <el-col :span="24">
                <ag-grid-vue
                    class="ag-theme-balham spreadsheet-table-grid"
                    :grid-options="gridOptions"
                    :column-defs="columnDefs"
                />
            </el-col>
        </el-row>
        <el-row type="flex" align="bottom" class="spreadsheet-bar">
            <el-col :span="6">
                <el-pagination
                    background
                    layout="prev, pager, next"
                    :total="pagesInfo.total"
                    :page-size="pageModel.pageSize"
                    :current-page="pageModel.currentPage"
                    :small="true"
                    @current-change="getCurrentPage"
                />
            </el-col>
            <el-col :span="9">
                <div class="spreadsheet-bar-pages">
                    <span class="spreadsheet-bar-pages-title">
                        Выводить по:
                    </span>
                    <span
                        class="spreadsheet-bar-pages-size"
                        :class="{'is-active': isSetPageSize20}"
                        @click="setPageSize(20)"
                    >
                        20
                    </span>
                    <span
                        class="spreadsheet-bar-pages-size"
                        :class="{'is-active': isSetPageSize50}"
                        @click="setPageSize(50)"
                    >
                        50
                    </span>
                    <span
                        class="spreadsheet-bar-pages-size"
                        :class="{'is-active': isSetPageSize100}"
                        @click="setPageSize(100)"
                    >
                        100
                    </span>
                </div>
            </el-col>
            <el-col :span="9">
                <div class="spreadsheet-bar-total">
                    <span>
                        Строк выведено: {{ totalRows }} Из них выделено: {{ selectedRowsCount }}
                    </span>
                </div>
            </el-col>
        </el-row>
    </el-container>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import TableHeaderSettings from '@/components/TableHeaderSettings';
import TableHeaderMessages from '@/components/TableHeaderMessages';
import DealGridHeader from './GridHeader';
import FileSaver from 'file-saver';
import { formatFinance, viewCustomFields, formatEmployerNameHtml } from '@/common/utilities';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import localReferences from '@/helpers/localReferences';
import '@/assets/styles/list.scss';

export default {
    name: 'DealsGrid',
    data() {
        return {
            headerOptions: {
                isUnreadMessagesCountVisible: true,
                isFilterVisible: true,
                isNewButtonVisible: true,
                isGoBackButtonVisible: false,
                isExcelButtonVisible: true
            },
            title: 'Список сделок',
            filterModel: {
                id: null,
                created_at: null,
                tags: [],
                pipeline_id: [],
                status_id: [],
                responsible_user_id: [],
                office_id: [],
                contact_id: [],
                contact_name: null,
                address: null,
                title: null,
                company_id: [],
                company_name: null,
                contract_number: null,
                contract_date: null,
                budget: null,
                link_id: [],
                volume: null,
                square_m2: null,
                finish: null,
                profile_type_id: [],
                jalousie: null,
                promising_customer: null,
                measurement_date: null,
                measurer_user_id: [],
                difficult_metering: null,
                measurer_bonus: null,
                conclusion_id: null,
                start_production_date: null,
                discount: null,
                prepay: null,
                pay_type_id: [],
                delivery_balance: null,
                installer_balance: null,
                in_production_id: [],
                production_cost: null,
                jalousie_cost: null,
                finish_cost: null,
                garbage_removal_id: [],
                delivery_date: null,
                installation_date: null,
                installation_user_id: [],
                installers_salary: null,
                installers_debt: null,
                additional_work: null,
                installation_done: null,
                complaint_opening_date: null,
                complaint_amount: null,
                complaint_materials_cost: null,
                guilty_id: null,
                complaint_performer_id: [],
                specialist_payment: null,
                complaint_closing_date: null,
                roistat: null,
                contact_address: null,
                contact_phone: null,
                contact_email: null,
                ltv_type: null,
                referral_full_name: null,
                referral_phone: null,
                ltv_certificate_number: null,
                nps_manager_id: [],
                nps_survey_date: null,
                nps_result: [],
                nps_client_score: [],
                nps_negative_reasons: [],
                nps_client_comment: null,
                nps_problem_status: [],
                nps_ltv_result: [],
                nps_ltv_certificate_number: null,
                nps_review_status: [],
                nps_review_link: null,
                nps_reward_status: [],
                nps_survey_manager_id: [],
                culprit_id: [],
                measurement_time: null,
                to_production: null,
                refuse_before_metering: null,
                tech_refusal: null,
                re_measurement: null,
                re_measurement_date: null,
                re_measurement_time: null,
                finish_amount: null,
                jalousie_amount: null,
                custom_fields: null
            },
            fieldsNames: {
                id: 'id',
                created_at: 'дата создания',
                tags: 'теги',
                pipeline_id: 'воронка',
                status_id: 'статус',
                unread_messages: 'непрочитанные сообщения',
                responsible_user_id: 'ответственный',
                office_id: 'офис',
                contact_id: 'контакт',
                contact_name: 'контакт',
                ltv_type: 'тип LTV',
                referral_full_name: 'ФИО рекомендателя',
                referral_phone: 'телефон рекомендателя',
                ltv_certificate_number: 'сертификат LTV №',
                nps_manager_id: 'менеджер NPS',
                nps_survey_date: 'дата опроса',
                nps_result: 'результат NPS',
                nps_client_score: 'оценка клиента',
                nps_negative_reasons: 'причина негативной оценки',
                nps_client_comment: 'комментарий клиента',
                nps_problem_status: 'статус решения проблемы',
                nps_ltv_result: 'результат LTV',
                nps_ltv_certificate_number: 'сертификат LTV №',
                nps_review_status: 'статус отзыва',
                nps_review_link: 'ссылка на отзыв',
                nps_reward_status: 'вознаграждение',
                nps_survey_manager_id: 'менеджер NPS',
                address: 'адрес',
                title: 'название',
                contract_number: 'договор №',
                contract_date: 'дата договора',
                budget: 'бюджет',
                link_id: 'канал связи',
                volume: 'объем',
                square_m2: 'квадратура  м2',
                finish: 'отделка',
                profile_type_name: 'тип профиля',
                profile_type_id: 'тип профиля',
                jalousie: 'жалюзи',
                promising_customer: 'перспективный заказчик',
                measurement_date: 'дата замера',
                measurer_user_id: 'замерщик',
                difficult_metering: 'сложный замер',
                measurer_bonus: 'бонус замерщика 1/2',
                conclusion_name: 'заключение',
                conclusion_id: 'заключение',
                start_production_date: 'дата запуска в производство',
                discount: 'скидка',
                prepay: 'предоплата',
                prepay_actual: 'предоплата факт',
                pay_type_id: 'вид оплаты',
                delivery_payment_plan: 'остаток по доставке план',
                delivery_balance: 'остаток по доставке',
                installer_balance: 'остаток по монтажу',
                in_production_id: 'в производстве',
                production_cost: 'сумма в производство  себестоимость',
                jalousie_cost: 'себестоимость жалюзи',
                finish_cost: 'себестоимость отделки',
                garbage_removal_id: 'вывоз мусора',
                delivery_date: 'дата доставки',
                installation_date: 'дата монтажа',
                installation_user_id: 'монтажная бригада',
                installers_salary: 'зп монтажников',
                installers_debt: 'долг монтажников',
                additional_work: 'доп. работы',
                installation_done: 'монтаж выполнен',
                complaint_opening_date: 'дата открытия рекламации',
                complaint_amount: 'сумма рекламации',
                complaint_materials_cost: 'сумма материалов для рекламации',
                guilty_id: 'кто виноват',
                complaint_performer_id: 'кто исправляет рекламацию',
                specialist_payment: 'оплата специалисту, который выполняет рекламацию',
                complaint_closing_date: 'дата закрытия рекламации',
                roistat: 'roistat',
                contact_phone: 'контакт – телефон',
                contact_email: 'контакт – e-mail',
                contact_address: 'контакт – адрес',
                culprit_id: 'Виновник',
                measurement_time: 'Время замера',
                to_production: 'В производство',
                refuse_before_metering: 'ОДЗ',
                tech_refusal: 'Тех отказ',
                re_measurement: 'Перезамер',
                re_measurement_date: 'Дата перезамера',
                re_measurement_time: 'Время перезамера',
                finish_amount: 'Сумма отделки',
                jalousie_amount: 'Сумма жалюзи',
                custom_fields: 'Дополнительные поля'
            },
            sortModel: { ...this.sortDefault },
            sortDefault: [
                {
                    colId: 'id', sort: 'desc'
                }
            ],
            sortByMeasurementDate: [
                {
                    colId: 'measurement_date', sort: 'desc'
                }
            ],
            sortByInstallationDate: [
                {
                    colId: 'installation_date', sort: 'desc'
                }
            ],
            pageModel: {
                currentPage: 1,
                pageSize: 20
            },
            totalRows: 0,
            selectedRowsCount: 0,
            loading: true,
            gridOptions: {
                context: {
                    showLtvColumns: false
                },
                rowSelection: 'multiple',
                suppressDragLeaveHidesColumns: false,
                suppressPaginationPanel: true,
                suppressLoadingOverlay: true,
                rowDeselection: true,
                enableCellTextSelection: true,
                headerHeight: 50,
                domLayout: 'autoHeight',
                onRowDataChanged: this.onRowDataChanged.bind(this),
                onRowSelected: this.onRowSelected.bind(this),
                onColumnResized: this.onColumnResized.bind(this),
                onDisplayedColumnsChanged: this.onDisplayedColumnsChanged.bind(this),
                onRowDoubleClicked: this.onSelectionChanged.bind(this),
                onSortChanged: this.onSortChanged.bind(this),
                defaultColDef: {
                    sortable: true,
                    filter: false,
                    resizable: true,
                    comparator: () => {
                        return 0;
                    },
                    cellClass: 'column-text',
                    hide: true
                },
                getRowHeight: params => {
                    const column = params.node.columnApi.getColumn('tags');
                    if (column && column.visible && params.data.tags && params.data.tags.length > 1) {
                        return 30 * params.data.tags.length;
                    } else {
                        return 40;
                    }
                }
            },
            columnDefs: [],
            firstRun: true
        };
    },
    components: {
        AgGridVue,
        DealGridHeader
    },
    computed: {
        deals() {
            return this.$store.state.deals.deals;
        },
        setting() {
            return this.$store.state.preferences.setting;
        },
        pagesInfo() {
            return this.$store.state.deals.pages;
        },
        isSetPageSize20() {
            return this.pageModel.pageSize === 20;
        },
        isSetPageSize50() {
            return this.pageModel.pageSize === 50;
        },
        isSetPageSize100() {
            return this.pageModel.pageSize === 100;
        }
    },
    created() {
        this.columnDefs = [
            {
                headerName: `${this.fieldsNames.id} сделки`,
                field: 'id',
                cellRenderer: ({ data }) => {
                    return this.getLinkElement(data.id,
                        data.id,
                        { name: 'deal-card', params: { id: data.id } },
                        'a', 'spreadsheet-table-id-link');
                },
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.unread_messages),
                field: 'unread_messages',
                cellRenderer: ({ data }) => {
                    if (data.unread_messages) {
                        return `<div class="flex-row-2col">
                                    <div class="flex-column envelop"></div>
                                    <div class="flex-column unread-msg">${data.unread_messages}</div>
                                 </div>`;
                    }
                },
                width: 50,
                headerComponentFramework: TableHeaderMessages,
                cellClass: null,
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.created_at),
                field: 'created_at',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.status_id),
                field: 'status',
                cellRenderer: ({ data }) => data.status ? `<div class="text-center deal-status-${data.status_id}">${data.status.name}</div>` : '',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.pipeline_id),
                field: 'pipeline',
                cellRenderer: ({ data }) => {
                    if (!data.pipeline) {
                        return 'Без воронки';
                    }
                    return data.pipeline.display_name || data.pipeline.name;
                },
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.installation_date),
                field: 'installation_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.responsible_user_id),
                field: 'responsible_user',
                cellRenderer: ({ data }) => formatEmployerNameHtml(data.responsible_user),
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.office_id),
                field: 'office',
                cellRenderer: ({ data }) => this.getOfficeName(data.office_id),
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.tags),
                field: 'tags',
                hide: false,
                filter: false,
                sortable: false,
                cellRenderer: ({ data }) => data.tags ? data.tags.map(item => `<div class="text-right orange">#${item.name}</div>`).join('') : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.contact_id),
                field: 'contact_name',
                cellRenderer: ({ data }) => data.contact ? data.contact.name : '',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.address),
                field: 'address',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.title),
                field: 'title',
                hide: false
            },
            {
                headerName: 'Тип LTV',
                field: 'ltv_type',
                ltvOnly: true,
                cellRenderer: ({ data }) => localReferences.ltvTypes[data.ltv_type] || ''
            },
            {
                headerName: 'ФИО рекомендателя',
                field: 'referral_full_name',
                ltvOnly: true
            },
            {
                headerName: 'Сертификат LTV №',
                field: 'ltv_certificate_number',
                ltvOnly: true
            },
            {
                headerName: 'Телефон рекомендателя',
                field: 'referral_phone',
                ltvOnly: true
            },
            {
                headerName: 'Менеджер NPS',
                field: 'nps_manager',
                ltvOnly: true,
                cellRenderer: ({ data }) => formatEmployerNameHtml(data.nps_manager)
            },
            {
                headerName: this.capitalize(this.fieldsNames.contract_number),
                field: 'contract_number',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.contract_date),
                field: 'contract_date',
                hide: false
            },
            {
                headerName: this.capitalize(this.fieldsNames.budget),
                field: 'budget',
                cellClass: 'text-right',
                hide: false,
                cellRenderer: ({ data }) => formatFinance(data.budget)
            },
            {
                headerName: this.capitalize(this.fieldsNames.link_id),
                field: 'link',
                cellRenderer: ({ data }) => data.link ? data.link.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.volume),
                field: 'volume'
            },
            {
                headerName: this.capitalize(this.fieldsNames.square_m2),
                field: 'square_m2'
            },
            {
                headerName: this.capitalize(this.fieldsNames.finish),
                field: 'finish',
                cellRenderer: ({ data }) => data.finish ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.finish_amount),
                field: 'finish_amount',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.finish_amount)
            },
            {
                headerName: this.capitalize(this.fieldsNames.profile_type_id),
                field: 'profile_type',
                cellRenderer: ({ data }) => data.profile_type ? data.profile_type.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.jalousie),
                field: 'jalousie',
                cellRenderer: ({ data }) => data.jalousie ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.jalousie_amount),
                field: 'jalousie_amount',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.jalousie_amount)
            },
            {
                headerName: this.capitalize(this.fieldsNames.promising_customer),
                field: 'promising_customer',
                cellRenderer: ({ data }) => data.promising_customer ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.to_production),
                field: 'to_production',
                cellRenderer: ({ data }) => data.to_production ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.measurement_date),
                field: 'measurement_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.measurement_time),
                field: 'measurement_time'
            },
            {
                headerName: this.capitalize(this.fieldsNames.measurer_user_id),
                field: 'measurer_user',
                cellRenderer: ({ data }) => formatEmployerNameHtml(data.measurer_user)
            },
            {
                headerName: this.capitalize(this.fieldsNames.difficult_metering),
                field: 'difficult_metering',
                cellRenderer: ({ data }) => data.difficult_metering ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.refuse_before_metering),
                field: 'refuse_before_metering',
                cellRenderer: ({ data }) => data.refuse_before_metering ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.tech_refusal),
                field: 'tech_refusal',
                cellRenderer: ({ data }) => data.tech_refusal ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.re_measurement),
                field: 're_measurement',
                cellRenderer: ({ data }) => data.re_measurement ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.re_measurement_date),
                field: 're_measurement_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.re_measurement_time),
                field: 're_measurement_time'
            },
            {
                headerName: this.capitalize(this.fieldsNames.measurer_bonus),
                field: 'measurer_bonus',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.measurer_bonus)
            },
            {
                headerName: this.capitalize(this.fieldsNames.conclusion_id),
                field: 'conclusion',
                cellRenderer: ({ data }) => data.conclusion ? data.conclusion.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.start_production_date),
                field: 'start_production_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.discount),
                field: 'discount',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.discount)
            },
            {
                headerName: this.capitalize(this.fieldsNames.prepay),
                field: 'prepay',
                cellClass: 'text-right',
                sortable: false,
                cellRenderer: ({ data }) => formatFinance(data.prepay)
            },
            {
                headerName: this.capitalize(this.fieldsNames.prepay_actual),
                field: 'prepay',
                cellClass: 'text-right',
                sortable: false,
                cellRenderer: ({ data }) => formatFinance(data.prepay_actual)
            },
            {
                headerName: this.capitalize(this.fieldsNames.pay_type_id),
                field: 'pay_type',
                cellRenderer: ({ data }) => data.pay_type ? data.pay_type.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.delivery_payment_plan),
                field: 'delivery_payment_plan',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.delivery_payment_plan)
            },
            {
                headerName: this.capitalize(this.fieldsNames.delivery_balance),
                field: 'delivery_balance',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.delivery_balance)
            },
            {
                headerName: this.capitalize(this.fieldsNames.installer_balance),
                field: 'installer_balance',
                sortable: false,
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.installer_balance)
            },
            {
                headerName: this.capitalize(this.fieldsNames.in_production_id),
                field: 'in_production',
                cellRenderer: ({ data }) => data.in_production ? data.in_production.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.production_cost),
                field: 'production_cost',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.production_cost)
            },
            {
                headerName: this.capitalize(this.fieldsNames.jalousie_cost),
                field: 'jalousie_cost',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.jalousie_cost)
            },
            {
                headerName: this.capitalize(this.fieldsNames.finish_cost),
                field: 'finish_cost',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.finish_cost)
            },
            {
                headerName: this.capitalize(this.fieldsNames.garbage_removal_id),
                field: 'garbage_removal',
                cellRenderer: ({ data }) => data.garbage_removal ? data.garbage_removal.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.delivery_date),
                field: 'delivery_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.installation_user_id),
                field: 'installation_user',
                cellRenderer: ({ data }) => formatEmployerNameHtml(data.installation_user)
            },
            {
                headerName: this.capitalize(this.fieldsNames.installers_salary),
                field: 'installers_salary',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.installers_salary)
            },
            {
                headerName: this.capitalize(this.fieldsNames.installers_debt),
                field: 'installers_debt',
                cellClass: 'text-right',
                sortable: false,
                cellRenderer: ({ data }) => formatFinance(data.installers_debt)
            },
            {
                headerName: this.capitalize(this.fieldsNames.additional_work),
                field: 'additional_work',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.additional_work)
            },
            {
                headerName: this.capitalize(this.fieldsNames.installation_done),
                field: 'installation_done',
                cellRenderer: ({ data }) => data.installation_done ? '<div class="check-icon-orange"></div>' : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.complaint_opening_date),
                field: 'complaint_opening_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.complaint_amount),
                field: 'complaint_amount',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.complaint_amount)
            },
            {
                headerName: this.capitalize(this.fieldsNames.guilty_id),
                field: 'guilty',
                cellRenderer: ({ data }) => data.guilty ? data.guilty.name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.culprit_id),
                field: 'culprit',
                cellRenderer: ({ data }) => data.culprit ? data.culprit.name || data.culprit.full_name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.complaint_performer_id),
                field: 'complaint_performer',
                cellRenderer: ({ data }) => data.complaint_performer ? data.complaint_performer.name || data.complaint_performer.full_name : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.specialist_payment),
                field: 'specialist_payment',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.specialist_payment)
            },
            {
                headerName: this.capitalize(this.fieldsNames.complaint_materials_cost),
                field: 'complaint_materials_cost',
                cellClass: 'text-right',
                cellRenderer: ({ data }) => formatFinance(data.complaint_materials_cost)
            },
            {
                headerName: this.capitalize(this.fieldsNames.complaint_closing_date),
                field: 'complaint_closing_date'
            },
            {
                headerName: this.capitalize(this.fieldsNames.roistat),
                field: 'roistat'
            },
            {
                headerName: this.capitalize(this.fieldsNames.contact_phone),
                field: 'contact_phone',
                cellRenderer: ({ data }) => data.contact && data.contact.contact_phones && data.contact.contact_phones.length ? data.contact.contact_phones[0].phone : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.contact_email),
                field: 'contact_email',
                cellRenderer: ({ data }) => data.contact ? data.contact.email : ''
            },
            {
                headerName: this.capitalize(this.fieldsNames.custom_fields),
                field: 'custom_fields',
                cellRenderer: ({ data }) => data.custom_fields ? viewCustomFields(data.custom_fields) : ''
            },
            {
                headerName: '',
                field: '',
                headerComponentFramework: TableHeaderSettings,
                cellRenderer: ({ data }) => {
                    return this.getLinkElement(data.id, '', { name: 'deal-card', params: { id: data.id } }, 'div', 'cell-icon');
                },
                width: 40,
                cellClass: null,
                hide: false
            }
        ];
    },
    mounted() {
        if (this.$route.query.pipeline_id !== undefined && this.$route.query.pipeline_id !== null && this.$route.query.pipeline_id !== '') {
            this.filterModel.pipeline_id = [Number(this.$route.query.pipeline_id)];
        }
        this.loadSettings();
    },
    methods: {
        loadSettings() {
            this.$store.dispatch('preferences/getPreferences').then(
                () => {
                    this.setSettings();
                }).catch(() => {
                this.applyLtvColumns();
                this.getDataFromServer();
            });
        },
        setSettings() {
            const settings = this.setting.deals;
            const pipelineId = this.$route.query.pipeline_id;
            if (settings) {
                const table = settings.table.map(column => Object.assign({}, column));
                if (this.$store.getters.isCurrentEmployerInstaller) {
                    table.find(column => column.colId === 'installation_date').hide = false;
                } else if (this.$store.getters.isCurrentEmployerMeasurer) {
                    table.find(column => column.colId === 'measurement_date').hide = false;
                }
                this.gridOptions.columnApi.setColumnState(table);
                let sort = [...settings.sort];
                if (this.firstRun) {
                    sort = this.getSort();
                    this.firstRun = false;
                }
                this.gridOptions.api.setSortModel(sort);
                this.filterModel = JSON.parse(JSON.stringify(settings.filter));
                if (pipelineId !== undefined && pipelineId !== null && pipelineId !== '') {
                    this.filterModel.pipeline_id = [Number(pipelineId)];
                    this.sanitizeStatusFilterForPipeline(pipelineId);
                } else {
                    this.filterModel.pipeline_id = [];
                }
                this.pageModel = JSON.parse(JSON.stringify(settings.page));
                this.resetContactFilter();
                this.applyLtvColumns();
            } else {
                this.gridOptions.api.setSortModel(this.getSort());
                this.applyLtvColumns();
            }
            this.getDataFromServer();
        },
        isCurrentLtvPipeline() {
            return localReferences.isLtvPipeline(this.$route.query.pipeline_id);
        },
        applyLtvColumns() {
            const columnApi = this.gridOptions.columnApi;
            if (!columnApi) {
                return;
            }
            const show = this.isCurrentLtvPipeline();
            this.gridOptions.context.showLtvColumns = show;
            if (!show) {
                localReferences.ltvColumnIds.forEach(colId => {
                    const column = columnApi.getColumn(colId);
                    if (column) {
                        columnApi.setColumnVisible(colId, false);
                    }
                });
                this.resetLtvFilters();
            } else {
                this.showLtvColumnsAfterTitle();
                this.ensureLtvFilters();
            }
            if (!localReferences.isNpsSearchPipeline(this.$route.query.pipeline_id)) {
                this.resetNpsFilters();
            } else {
                this.ensureNpsFilters();
            }
        },
        showLtvColumnsAfterTitle() {
            const columnApi = this.gridOptions.columnApi;
            const visibleIds = localReferences.ltvVisibleColumnIds;
            visibleIds.forEach(colId => {
                if (columnApi.getColumn(colId)) {
                    columnApi.setColumnVisible(colId, true);
                }
            });
            for (let i = visibleIds.length - 1; i >= 0; i--) {
                const colId = visibleIds[i];
                if (!columnApi.getColumn(colId)) {
                    continue;
                }
                const allColumns = columnApi.getAllGridColumns();
                const titleIndex = allColumns.findIndex(col => col.getColId() === 'title');
                if (titleIndex === -1) {
                    return;
                }
                columnApi.moveColumn(colId, titleIndex + 1);
            }
        },
        ensureLtvFilters() {
            if (this.filterModel.ltv_type === undefined) {
                this.$set(this.filterModel, 'ltv_type', null);
            }
            if (this.filterModel.referral_full_name === undefined) {
                this.$set(this.filterModel, 'referral_full_name', null);
            }
            if (this.filterModel.referral_phone === undefined) {
                this.$set(this.filterModel, 'referral_phone', null);
            }
            if (this.filterModel.ltv_certificate_number === undefined) {
                this.$set(this.filterModel, 'ltv_certificate_number', null);
            }
            if (!Array.isArray(this.filterModel.nps_manager_id)) {
                this.$set(this.filterModel, 'nps_manager_id', []);
            }
        },
        resetLtvFilters() {
            this.filterModel.ltv_type = null;
            this.filterModel.referral_full_name = null;
            this.filterModel.referral_phone = null;
            this.filterModel.ltv_certificate_number = null;
            this.filterModel.nps_manager_id = [];
        },
        ensureNpsFilters() {
            Object.keys(localReferences.npsFilterDefaults).forEach(key => {
                if (this.filterModel[key] === undefined) {
                    const def = localReferences.npsFilterDefaults[key];
                    this.$set(this.filterModel, key, Array.isArray(def) ? [] : def);
                }
            });
        },
        resetNpsFilters() {
            Object.keys(localReferences.npsFilterDefaults).forEach(key => {
                const def = localReferences.npsFilterDefaults[key];
                this.$set(this.filterModel, key, Array.isArray(def) ? [] : def);
            });
        },
        getSort() {
            if (this.$store.getters.isCurrentEmployerMeasurer) {
                return this.sortByMeasurementDate;
            } else if (this.$store.getters.isCurrentEmployerInstaller) {
                return this.sortByInstallationDate;
            }
            return this.sortDefault;
        },
        resetContactFilter() {
            this.filterModel.contact_id = null;
            this.filterModel.contact_address = null;
            this.filterModel.contact_email = null;
            this.filterModel.contact_name = null;
            this.filterModel.contact_phone = null;
        },
        sanitizeStatusFilterForPipeline(pipelineId) {
            if (pipelineId === undefined || pipelineId === null || pipelineId === '') {
                return;
            }
            const statusFilter = this.filterModel.status_id;
            if (!Array.isArray(statusFilter) || statusFilter.length === 0) {
                return;
            }
            const validStatusIds = localReferences.getStatusesForPipeline(pipelineId);
            this.filterModel.status_id = statusFilter.filter(id => validStatusIds.indexOf(Number(id)) !== -1);
        },
        getLinkElement(id, name, route, elementType, className) {
            const element = document.createElement(elementType);
            if (elementType === 'a') {
                element.href = this.$router.resolve(route).href;
                element.innerText = name;
            }
            element.className += ' ' + className;
            element.addEventListener('click', e => {
                e.preventDefault();
                this.$router.push(route);
            });
            return element;
        },
        onSelectionChanged(e) {
            const deal = e.node.data || {};
            const pipelineId = (deal.pipeline_id !== undefined && deal.pipeline_id !== null && deal.pipeline_id !== '')
                ? deal.pipeline_id
                : this.$route.query.pipeline_id;
            const query = Object.assign({}, this.$route.query);
            if (pipelineId !== undefined && pipelineId !== null && pipelineId !== '') {
                query.pipeline_id = pipelineId;
            }
            this.$router.push({
                name: 'deal-card',
                params: { id: '' + deal.id },
                query
            }).catch(() => {});
        },
        getDataFromServer() {
            const requestParams = {
                size: this.pageModel.pageSize,
                page: this.pageModel.currentPage,
                sort: this.gridOptions.api.getSortModel(),
                filter: this.filterModel
            };
            this.loading = true;
            this.$store.dispatch('deals/getPaginatedList', requestParams).then(
                response => {
                    this.gridOptions.api.setRowData(this.deals);
                    this.pageModel.pageSize = +response.data.meta.per_page;
                    this.pageModel.currentPage = response.data.meta.current_page;
                    this.saveSetting();
                    this.loading = false;
                }).catch(
                error => {
                    console.log(error);
                });
        },
        setPageSize(size) {
            this.pageModel.pageSize = size;
            this.getDataFromServer();
        },
        getCurrentPage(page) {
            this.pageModel.currentPage = page;
            this.getDataFromServer();
        },
        getOfficeName(officeId) {
            const office = this.$store.state.references.offices.find(office => office.id === officeId);
            return office ? office.name : '';
        },
        filterApplied(data) {
            this.filterModel = data;
            this.pageModel.currentPage = 1;
            this.getDataFromServer();
        },
        onRowDataChanged() {
            this.totalRows = this.gridOptions.api.getDisplayedRowCount();
        },
        onRowSelected() {
            this.selectedRowsCount = this.gridOptions.api.getSelectedRows().length;
        },
        onDisplayedColumnsChanged() {
            this.saveSetting();
        },
        onColumnResized(data) {
            if (data.finished && data.source === 'uiColumnDragged') {
                this.saveSetting();
            }
        },
        onSortChanged() {
            this.getDataFromServer();
        },
        async saveSetting() {
            const data = {
                'deals': {
                    'table': this.gridOptions.columnApi.getColumnState(),
                    'sort': this.gridOptions.api.getSortModel(),
                    'filter': this.filterModel,
                    'page': this.pageModel
                }
            };
            await this.$store.dispatch('preferences/setPreferences', data);
        },
        exportDeals() {
            const requestParams = {
                sort: this.gridOptions.api.getSortModel(),
                filter: this.filterModel,
                filename: `Сделки_${this.pagesInfo.total}_(${moment().format('DD.MM.YY_HHmmss')}).xlsx`
            };
            this.loading = true;
            this.$store.dispatch('deals/getDealsExportedToExcel', requestParams).then(response => {
                FileSaver(new Blob([response.data], { type: 'application/vnd.ms-excel' }), requestParams.filename);
            }).catch(error => {
                console.log(error);
            }).finally(() => {
                this.loading = false;
            });
        },
        capitalize
    },
    watch: {
        '$route.query.pipeline_id': function(pipelineId) {
            this.filterModel.pipeline_id = (pipelineId !== undefined && pipelineId !== null && pipelineId !== '')
                ? [Number(pipelineId)]
                : [];
            this.sanitizeStatusFilterForPipeline(pipelineId);
            this.applyLtvColumns();
            this.pageModel.currentPage = 1;
            this.getDataFromServer();
            this.saveSetting();
        }
    }
};
</script>
